import React, { useMemo, useState } from "react";
import { ApiError, createWishlist } from "@/lib/api";
import { haptic, openExternalLink } from "@/lib/telegram";
import { TopBar } from "@/components/TopBar";
import { ProductCard } from "@/components/ProductCard";
import { EmptyState } from "@/components/EmptyState";
import { useI18n } from "@/i18n";
import { useRouter } from "@/router/Router";
import { useCart } from "@/state/CartContext";
import { useCatalog } from "@/state/CatalogContext";
import { useBackButton } from "@/lib/hooks";

export function FavoritesPage() {
  const { t } = useI18n();
  const { goBack, navigate } = useRouter();
  const { favorites } = useCart();
  const { products: catalogProducts } = useCatalog();
  const products = useMemo(() => catalogProducts.filter((p) => favorites.includes(p.id)), [catalogProducts, favorites]);

  useBackButton(goBack);
  const [sharing, setSharing] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);

  const share = async () => {
    setSharing(true);
    setShareError(null);
    try {
      const { link } = await createWishlist(products.map((p) => p.id));
      if (!link) throw new Error("no link");
      haptic("light");
      openExternalLink(`https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(t("wishlist.shareText"))}`);
    } catch (err) {
      setShareError(err instanceof ApiError ? err.message : t("common.error"));
    } finally {
      setSharing(false);
    }
  };

  return (
    <>
      <TopBar title={t("profile.favorites")} onBack={goBack} />
      <main className="page page--no-nav">
        {products.length === 0 ? (
          <EmptyState
            art="favorites"
            title={t("profile.favoritesEmpty")}
            action={
              <button className="btn btn--primary" onClick={() => navigate({ name: "catalog" })}>
                {t("cart.goToCatalog")}
              </button>
            }
          />
        ) : (
          <>
            <div className="share-card">
              <div>
                <strong>{t("wishlist.shareTitle")}</strong>
                <span>{t("wishlist.shareBody")}</span>
              </div>
              <button className="btn btn--primary" style={{ width: "auto", minHeight: 40, padding: "8px 16px" }} disabled={sharing} onClick={share}>
                {t("wishlist.share")}
              </button>
            </div>
            {shareError && <div className="form-error-banner">{shareError}</div>}
            <div className="grid-2">
              {products.map((p) => (
                <ProductCard key={p.id} product={p} width="100%" />
              ))}
            </div>
          </>
        )}
      </main>
    </>
  );
}
